// Noise tokens stripped from raw provider titles. Resolution, codec, source,
// and release-group suffixes add nothing to TMDB matching.
const NOISE_PATTERN = /\b(2160p|1080p|720p|480p|4k|uhd|bluray|blu-ray|web-?dl|webrip|hdtv|h264|x264|x265|hevc|hvec|aac|dd5\.1|dts|remux|proper|repack|extended|uncut|unrated|multisub)\b/gi;
// Bracketed prefix tags on raw provider titles: "[ES] El irlandes",
// "[FRENCH] F1 Grand Prix", "(REC) ...". Only a short, space-free token
// qualifies, so a title that genuinely opens with a bracket -- such as
// "(No Subject) (2021)" -- keeps its own words.
const TAG_PATTERN = /^[\s|]*[[(|]\s*([^\s\])|]{1,8})\s*[\])|]\s*/;
/**
 * Provider language tags mapped to the TMDB `language` parameter. Measured on
 * a 52,025-title catalog: 147 titles carry a tag, dominated by ES (48), NL
 * (45) and FR (39).
 *
 * This matters because matchToTMDB scores the cleaned title against the title
 * TMDB returns. Searched without a language, "El irlandes" yields "The
 * Irishman" (Dice 0.29, below MATCH_THRESHOLD, rejected) while the unrelated
 * "El prisionero irlandes" scores 0.65 and would be accepted -- a confidently
 * wrong poster. Searched with es-ES, TMDB returns the localized title
 * "El irlandes" (Dice 1.0) and the correct film wins outright.
 *
 * Tags absent from this map (REC, CAM, TS, 4K...) are source or quality
 * markers rather than languages: they are still stripped from the query, but
 * they do not localize it.
 */
const LANGUAGE_TAGS = {
    es: "es-ES",
    fr: "fr-FR",
    french: "fr-FR",
    vf: "fr-FR",
    nl: "nl-NL",
    de: "de-DE",
    it: "it-IT",
    pt: "pt-PT",
    br: "pt-BR",
    ru: "ru-RU",
    pl: "pl-PL",
    tr: "tr-TR",
    ar: "ar-SA",
};
/**
 * Clean a raw provider title like `The.Matrix.1999.1080p.BluRay.x264` into
 * `{ title: "The Matrix", year: 1999 }` — the key for TMDB search and the
 * cache key for match results.
 */
export function cleanTitle(raw) {
    // Strip leading bracketed tags first, remembering the first one that names a
    // language so the TMDB search can be localized.
    let source = raw;
    let language;
    for (;;) {
        const tag = TAG_PATTERN.exec(source);
        if (!tag?.[1])
            break;
        const mapped = LANGUAGE_TAGS[tag[1].toLowerCase()];
        if (mapped && !language)
            language = mapped;
        source = source.slice(tag[0].length);
    }
    // The *last* standalone year is the release year: in "2001 A Space Odyssey
    // 1968", 1968 is the release and 2001 belongs to the title. Using the last
    // match (instead of the first, as in the spec's sample) handles that.
    const years = source.match(/\b(19|20)\d{2}\b/g);
    const year = years && years.length > 0 ? Number(years[years.length - 1]) : undefined;
    let title = source
        .replace(/\.(mkv|mp4|avi|mpg|mpeg|ts|m2ts|wmv|flv|mov|webm)$/i, "") // trailing container extension only — never a word like "Year"
        .replace(NOISE_PATTERN, " ")
        .replace(/[._-]/g, " ")
        // Normalize remaining punctuation (:, &, ', …) to spaces. Unicode-aware:
        // the previous ASCII-only [^\w\s] deleted every accented letter, turning
        // "Amélie" into "Am lie" and "El irlandés" into "El irland s". 2,558 of
        // 52,025 catalog titles (4.9%) contain non-ASCII letters, so all of them
        // reached TMDB mangled and matched nothing.
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
    if (year !== undefined) {
        // Drop the extracted year from the title so the TMDB query and the cache
        // key are clean ("The Matrix" + year=1999, not "The Matrix 1999").
        // Global: providers commonly stamp the year twice ("(No Subject) (2021) -
        // 2021"), and a non-global replace left the second copy in the query.
        title = title.replace(new RegExp(`\\b${year}\\b`, "g"), " ").replace(/\s{2,}/g, " ").trim();
    }
    return { title, year, ...(language ? { language } : {}) };
}
/** Extract a S\d+E\d+ marker from a raw series episode title, if present. */
export function extractSeriesSeEp(raw) {
    const match = raw.match(/\bs(\d{1,3})\s*e(\d{1,3})\b/i);
    if (!match)
        return null;
    return {
        ...(match[1] ? { season: Number(match[1]) } : {}),
        ...(match[2] ? { episode: Number(match[2]) } : {}),
    };
}
/**
 * Dice coefficient (bigram overlap) — a cheap fuzzy-match ratio good for
 * short titles. Returns 0..1.
 */
export function diceCoefficient(a, b) {
    const left = a.toLowerCase();
    const right = b.toLowerCase();
    if (left === right)
        return 1;
    if (left.length < 2 || right.length < 2)
        return 0;
    const bigrams = (value) => {
        const counts = new Map();
        for (let i = 0; i < value.length - 1; i += 1) {
            const pair = value.slice(i, i + 2);
            counts.set(pair, (counts.get(pair) ?? 0) + 1);
        }
        return counts;
    };
    const aBigrams = bigrams(left);
    const bBigrams = bigrams(right);
    let overlap = 0;
    for (const [pair, count] of aBigrams) {
        const other = bBigrams.get(pair) ?? 0;
        overlap += Math.min(count, other);
    }
    return (2 * overlap) / (left.length - 1 + right.length - 1);
}
/** Minimum confidence for a match to be accepted — below this, unmatched. */
export const MATCH_THRESHOLD = 0.6;
/**
 * Match a raw provider title to a TMDB entry. Clean the title, search TMDB
 * (passing the extracted year for precision), and accept the top result only
 * when the Dice coefficient clears MATCH_THRESHOLD. Below threshold — or no
 * results — returns null: a confidently-wrong match is worse than an honest
 * "no artwork available" state.
 */
export async function matchToTMDB(rawTitle, tmdb, media = "movie") {
    const { title, year, language } = cleanTitle(rawTitle);
    if (!title)
        return null;
    // IPTV catalogs frequently carry wrong years. Search with the extracted
    // year for precision, but if it returns nothing, retry without the year
    // before declaring unmatched — a false null is just as bad as a wrong
    // match, and this halves the false-null rate on mistitled/dated rips.
    let results = await tmdb.search(media, title, year, language);
    if (!results.length && year !== undefined) {
        results = await tmdb.search(media, title, undefined, language);
    }
    if (!results.length)
        return null;
    let best = null;
    for (const result of results) {
        const score = diceCoefficient(title, result.title);
        if (!best || score > best.score)
            best = { result, score };
    }
    if (!best || best.score < MATCH_THRESHOLD)
        return null;
    return { tmdbId: best.result.id, confidence: best.score, summary: best.result };
}
//# sourceMappingURL=matching.js.map